import React, { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { X, Users, GitMerge, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MERGE_FIELDS = [
  { key: 'title', label: 'Title' },
  { key: 'organization', label: 'Organization' },
  { key: 'image_url', label: 'Photo URL' },
  { key: 'bio', label: 'Bio' },
  { key: 'status', label: 'Status' },
  { key: 'tier', label: 'Tier' },
  { key: 'is_featured', label: 'Featured' },
];

const isEmptyValue = (field, value) => {
  if (value === null || value === undefined) return true;
  if (field === 'bio') {
    const s = String(value).trim().toLowerCase();
    return s === '' || s === 'bio coming soon.';
  }
  if (field === 'is_featured') return false; // boolean is never "empty"
  return String(value).trim() === '';
};

const prettyValue = (field, value) => {
  if (value === null || value === undefined || value === '') return <span className="italic text-gray-400">empty</span>;
  if (field === 'is_featured') return value ? 'Yes' : 'No';
  if (field === 'image_url') {
    return (
      <div className="flex items-center gap-2 min-w-0">
        <img src={value} alt="" className="w-8 h-8 rounded-full object-cover border" />
        <span className="truncate text-xs text-gray-600">{value}</span>
      </div>
    );
  }
  if (field === 'bio') {
    const text = String(value);
    return <span title={text} className="line-clamp-2 block">{text}</span>;
  }
  return String(value);
};

/**
 * Modal UI for finding + merging duplicate speakers.
 * Flow: list duplicate groups → admin picks a keeper → optional per-field overrides
 *       → POST /api/speakers/duplicates/merge → refresh.
 */
const MergeDuplicatesModal = ({ open, onClose, onMerged }) => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  // Per-group state: { keepId, overrides: {field: value}, busy: bool }
  const [groupState, setGroupState] = useState({});

  const fetchDuplicates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/speakers/duplicates/list`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to load duplicates');
      const data = await res.json();
      const list = data.groups || [];
      setGroups(list);
      // Seed default keeper = first speaker; no overrides.
      const seeded = {};
      for (const g of list) {
        seeded[g.normalized_name] = {
          keepId: g.speakers[0]?.id || '',
          overrides: {},
        };
      }
      setGroupState(seeded);
    } catch (e) {
      toast.error(e.message || 'Could not load duplicates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchDuplicates();
  }, [open]);

  const setKeeper = (normName, id) => {
    setGroupState(prev => ({
      ...prev,
      [normName]: { ...(prev[normName] || { overrides: {} }), keepId: id, overrides: {} },
    }));
  };

  const setOverride = (normName, field, value) => {
    setGroupState(prev => {
      const cur = prev[normName] || { keepId: '', overrides: {} };
      const nextOverrides = { ...cur.overrides };
      if (value === '__KEEP__') {
        delete nextOverrides[field];
      } else {
        // Decode speaker-id → look up the value from that speaker
        nextOverrides[field] = value;
      }
      return { ...prev, [normName]: { ...cur, overrides: nextOverrides } };
    });
  };

  const mergeGroup = async (group) => {
    const state = groupState[group.normalized_name];
    const keepId = state?.keepId;
    if (!keepId) {
      toast.error('Pick which speaker to keep first');
      return;
    }
    const delete_ids = group.speakers.filter(s => s.id !== keepId).map(s => s.id);
    if (delete_ids.length === 0) {
      toast.error('Nothing to merge in this group');
      return;
    }
    setGroupState(prev => ({ ...prev, [group.normalized_name]: { ...state, busy: true } }));
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/speakers/duplicates/merge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          keep_id: keepId,
          delete_ids,
          overrides: state.overrides || {},
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Merge failed');
      toast.success(`Merged ${delete_ids.length} duplicate${delete_ids.length === 1 ? '' : 's'} into "${data.merged_into?.name || 'speaker'}"`);
      // Refresh list + notify parent to reload speakers.
      await fetchDuplicates();
      onMerged?.();
    } catch (e) {
      toast.error(e.message || 'Merge failed');
    } finally {
      setGroupState(prev => {
        const cur = prev[group.normalized_name] || {};
        return { ...prev, [group.normalized_name]: { ...cur, busy: false } };
      });
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      data-testid="merge-duplicates-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-[#00457f] to-[#0a5fa8] text-white">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5" />
            <div>
              <h2 className="text-lg font-semibold">Merge Duplicate Speakers</h2>
              <p className="text-xs text-blue-100">Review groups with matching names and combine them</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-md transition"
            data-testid="merge-duplicates-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Scanning for duplicates…
            </div>
          ) : groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center text-gray-600">
              <CheckCircle2 className="w-10 h-10 text-green-500 mb-3" />
              <p className="text-base font-medium">No duplicate speakers found.</p>
              <p className="text-sm text-gray-500 mt-1">Names are matched after normalising case, honorifics, and accents.</p>
            </div>
          ) : (
            groups.map((group) => {
              const state = groupState[group.normalized_name] || { keepId: '', overrides: {} };
              const keeper = group.speakers.find(s => s.id === state.keepId) || group.speakers[0];
              // Fields where the group has disagreement OR keeper is empty.
              const relevantFields = MERGE_FIELDS.filter(f => {
                const values = group.speakers.map(s => s[f.key]);
                const nonEmpty = values.filter(v => !isEmptyValue(f.key, v));
                // Show if there is at least one non-empty value AND there is disagreement
                // (either keeper empty with another having value, or distinct values).
                if (nonEmpty.length === 0) return false;
                const distinct = new Set(nonEmpty.map(v => (typeof v === 'object' ? JSON.stringify(v) : String(v))));
                const keeperEmpty = isEmptyValue(f.key, keeper?.[f.key]);
                return keeperEmpty || distinct.size > 1;
              });

              return (
                <div
                  key={group.normalized_name}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
                  data-testid={`duplicate-group-${group.normalized_name.replace(/\s+/g, '-')}`}
                >
                  <div className="px-5 py-3 border-b bg-amber-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-900">
                        {group.speakers.length} speakers match: "{group.speakers[0]?.name}"
                      </span>
                    </div>
                    <Button
                      size="sm"
                      disabled={state.busy || !state.keepId}
                      onClick={() => mergeGroup(group)}
                      className="bg-[#00457f] hover:bg-[#003461] text-white flex items-center gap-2"
                      data-testid={`merge-group-btn-${group.normalized_name.replace(/\s+/g, '-')}`}
                    >
                      {state.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />}
                      Merge {group.speakers.length - 1} into keeper
                    </Button>
                  </div>

                  {/* Keeper picker */}
                  <div className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                      1. Pick the speaker to keep
                    </p>
                    <div className="grid gap-2 md:grid-cols-2">
                      {group.speakers.map((s) => {
                        const isKeeper = s.id === state.keepId;
                        return (
                          <label
                            key={s.id}
                            className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition ${
                              isKeeper ? 'border-[#00457f] bg-blue-50 ring-2 ring-[#00457f]/20' : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                            data-testid={`keeper-option-${s.id}`}
                          >
                            <input
                              type="radio"
                              name={`keeper-${group.normalized_name}`}
                              checked={isKeeper}
                              onChange={() => setKeeper(group.normalized_name, s.id)}
                              className="mt-1 accent-[#00457f]"
                            />
                            <div className="flex gap-3 min-w-0 flex-1">
                              {s.image_url ? (
                                <img src={s.image_url} alt={s.name} className="w-10 h-10 rounded-full object-cover border" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm font-semibold">
                                  {(s.name || '?').charAt(0)}
                                </div>
                              )}
                              <div className="min-w-0 flex-1 text-sm">
                                <div className="font-medium text-gray-900 truncate">{s.name}</div>
                                <div className="text-gray-600 truncate">{s.title || <span className="italic text-gray-400">no title</span>}</div>
                                <div className="text-gray-500 truncate text-xs">{s.organization || <span className="italic text-gray-400">no org</span>}</div>
                                <div className="text-xs text-gray-400 mt-1">ID: {s.id.slice(0, 8)}…</div>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>

                    {/* Field conflict picker */}
                    {relevantFields.length > 0 && (
                      <>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-5 mb-2">
                          2. For each field, pick which value wins (default keeps keeper)
                        </p>
                        <div className="space-y-2">
                          {relevantFields.map((f) => {
                            const current = state.overrides?.[f.key];
                            const keeperVal = keeper?.[f.key];
                            // Options: keeper value + every unique non-empty value from others
                            const seen = new Set();
                            const options = [];
                            group.speakers.forEach((sp) => {
                              const val = sp[f.key];
                              if (sp.id === state.keepId) return; // keeper handled by default row
                              if (isEmptyValue(f.key, val)) return;
                              const key = typeof val === 'object' ? JSON.stringify(val) : String(val);
                              if (seen.has(key)) return;
                              seen.add(key);
                              options.push({ fromName: sp.name, fromId: sp.id, value: val });
                            });
                            if (options.length === 0) return null;
                            return (
                              <div
                                key={f.key}
                                className="grid grid-cols-[120px_1fr] gap-3 items-start text-sm py-2 border-t border-gray-100"
                                data-testid={`field-row-${f.key}`}
                              >
                                <div className="font-medium text-gray-700 pt-1">{f.label}</div>
                                <div className="space-y-1">
                                  <label className={`flex items-start gap-2 p-2 rounded cursor-pointer ${current === undefined ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                                    <input
                                      type="radio"
                                      name={`field-${group.normalized_name}-${f.key}`}
                                      checked={current === undefined}
                                      onChange={() => setOverride(group.normalized_name, f.key, '__KEEP__')}
                                      className="mt-1 accent-[#00457f]"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs text-gray-500 mb-0.5">Keeper's value</div>
                                      <div className="text-gray-800">{prettyValue(f.key, keeperVal)}</div>
                                    </div>
                                  </label>
                                  {options.map((opt, idx) => {
                                    const isSelected = current !== undefined && (typeof opt.value === 'object' ? JSON.stringify(current) === JSON.stringify(opt.value) : String(current) === String(opt.value));
                                    return (
                                      <label
                                        key={`${f.key}-${opt.fromId}-${idx}`}
                                        className={`flex items-start gap-2 p-2 rounded cursor-pointer ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                      >
                                        <input
                                          type="radio"
                                          name={`field-${group.normalized_name}-${f.key}`}
                                          checked={isSelected}
                                          onChange={() => setOverride(group.normalized_name, f.key, opt.value)}
                                          className="mt-1 accent-[#00457f]"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <div className="text-xs text-gray-500 mb-0.5">From duplicate</div>
                                          <div className="text-gray-800">{prettyValue(f.key, opt.value)}</div>
                                        </div>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-white flex items-center justify-between">
          <div className="text-xs text-gray-500">
            Blank fields on the keeper are always filled from the duplicates automatically.
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchDuplicates} disabled={loading} data-testid="merge-duplicates-rescan">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Rescan
            </Button>
            <Button onClick={onClose} data-testid="merge-duplicates-done">Done</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MergeDuplicatesModal;
